/**
 * Staff (sub_admin) permissions
 */
export const STAFF_PERMISSIONS = {
  // User management
  CAN_VIEW_USERS: 'canViewUsers',
  CAN_MANAGE_USERS: 'canManageUsers',
  
  // Company management
  CAN_VIEW_COMPANIES: 'canViewCompanies',
  CAN_MANAGE_COMPANIES: 'canManageCompanies',
  
  // Order management
  CAN_VIEW_ORDERS: 'canViewOrders',
  CAN_MANAGE_ORDERS: 'canManageOrders',
  
  // Project management
  CAN_VIEW_PROJECTS: 'canViewProjects',
  CAN_MANAGE_PROJECTS: 'canManageProjects',
  
  // Financial
  CAN_VIEW_FINANCIALS: 'canViewFinancials',
  CAN_MANAGE_BILLING: 'canManageBilling',
  
  // Subscription management
  CAN_VIEW_SUBSCRIPTIONS: 'canViewSubscriptions',
  CAN_MANAGE_SUBSCRIPTIONS: 'canManageSubscriptions',

  // Staff management
  CAN_VIEW_STAFF: "canViewStaff",
  CAN_MANAGE_STAFF: "canManageStaff",

  // Settings management
  CAN_VIEW_SETTINGS: "canViewSettings",
  CAN_MANAGE_SETTINGS: "canManageSettings",

  // Offer management
  CAN_VIEW_OFFER: "canViewOffer",
  CAN_MANAGE_OFFER: "canManageOffer",
};

/**
 * Permission groups for common roles
 * Maps team member role IDs to permission sets
 */
export const PERMISSION_PRESETS = {
    // Content Manager - basic viewing + order management
  CONTENT_MANAGER: {
    canViewUsers: true,
    canManageUsers: false,
    canViewCompanies: true,
    canManageCompanies: false,
    canViewOrders: true,
    canManageOrders: true,
    canViewProjects: true,
    canManageProjects: false,
    canViewFinancials: false,
    canManageBilling: false,
    canViewSubscriptions: true,
    canManageSubscriptions: false,
    canViewSettings: false,
    canManageSettings: false,
    canViewTeam: false,
    canManageTeam: false,
    canViewOffer: false,
    canManageOffer: false,
    canViewStaff: false,
    canManageStaff: false,
  },
  
  // Account Manager - full client management
  ACCOUNT_MANAGER: {
    canViewUsers: true,
    canManageUsers: true,
    canViewCompanies: true,
    canManageCompanies: true,
    canViewOrders: true,
    canManageOrders: true,
    canViewProjects: true,
    canManageProjects: true,
    canViewFinancials: false,
    canManageBilling: false,
    canViewSubscriptions: true,
    canManageSubscriptions: false,
    canViewSettings: false,
    canManageSettings: false,
    canViewTeam: true,
    canManageTeam: false,
    canViewOffer: true,
    canManageOffer: true,
    canViewStaff: true,
    canManageStaff: false,
  },
  
  // Finance Admin - financial access
  FINANCE_ADMIN: {
    canViewUsers: true,
    canManageUsers: false,
    canViewCompanies: true,
    canManageCompanies: false,
    canViewOrders: true,
    canManageOrders: false,
    canViewProjects: true,
    canManageProjects: false,
    canViewFinancials: true,
    canManageBilling: true,
    canViewSubscriptions: true,
    canManageSubscriptions: true,
    canViewSettings: false,
    canManageSettings: false,
    canViewTeam: false,
    canManageTeam: false,
    canViewOffer: false,
    canManageOffer: false,
    canViewStaff: false,
    canManageStaff: false
  },
  
  // Developer - project-focused access
  DEVELOPER: {
    canViewUsers: false,
    canManageUsers: false,
    canViewCompanies: false,
    canManageCompanies: false,
    canViewOrders: true,          // Can view orders
    canManageOrders: false,
    canViewProjects: true,         // Can view projects
    canManageProjects: true,       // Can manage assigned projects
    canViewFinancials: false,
    canManageBilling: false,
    canViewSubscriptions: true,    // Can view subscriptions
    canManageSubscriptions: false,
    canViewSettings: false,
    canManageSettings: false,
    canViewTeam: false,
    canManageTeam: false,
    canViewOffer: false,
    canManageOffer: false,
    canViewStaff: false,
    canManageStaff: false
  },
  
  // Manager - team and project management
  MANAGER: {
    canViewUsers: true,
    canManageUsers: true, // Can manage team members
    canViewCompanies: true,
    canManageCompanies: true,
    canViewOrders: true,
    canManageOrders: true,
    canViewProjects: true,
    canManageProjects: true,
    canViewFinancials: true,
    canManageBilling: false,
    canViewSubscriptions: true,
    canManageSubscriptions: false,
    canViewOffer: true,
    canManageOffer: true,
    canViewStaff: true,
    canManageStaff: true
  },
  
  // Business Analyst - client and project access
  BUSINESS_ANALYST: {
    canViewUsers: true,
    canManageUsers: false,
    canViewCompanies: true,
    canManageCompanies: true,
    canViewOrders: true,
    canManageOrders: false,
    canViewProjects: true,
    canManageProjects: true,
    canViewFinancials: false,
    canManageBilling: false,
    canViewSubscriptions: true,
    canManageSubscriptions: false,
    canViewOffer: true,
    canManageOffer: false,
    canViewStaff: false,
    canManageStaff: false
  },
  
  // UI/UX Engineer - project-focused
  UI_UX_ENGINEER: {
    canViewUsers: true,
    canManageUsers: false,
    canViewCompanies: true,
    canManageCompanies: false,
    canViewOrders: true,
    canManageOrders: false,
    canViewProjects: true,
    canManageProjects: true,
    canViewFinancials: false,
    canManageBilling: false,
    canViewSubscriptions: false,
    canManageSubscriptions: false,
    canViewOffer: false,
    canManageOffer: false,
    canViewStaff: false,
    canManageStaff: false
  },
  
  // QA Engineer - project-focused
  QA_ENGINEER: {
    canViewUsers: true,
    canManageUsers: false,
    canViewCompanies: true,
    canManageCompanies: false,
    canViewOrders: true,
    canManageOrders: false,
    canViewProjects: true,
    canManageProjects: true,
    canViewFinancials: false,
    canManageBilling: false,
    canViewSubscriptions: false,
    canManageSubscriptions: false,
    canViewOffer: false,
    canManageOffer: false,
    canViewStaff: false,
    canManageStaff: false
  },
  
  // DevOps Engineer - project and system access
  DEVOPS_ENGINEER: {
    canViewUsers: true,
    canManageUsers: false,
    canViewCompanies: true,
    canManageCompanies: false,
    canViewOrders: true,
    canManageOrders: false,
    canViewProjects: true,
    canManageProjects: true,
    canViewFinancials: false,
    canManageBilling: false,
    canViewSubscriptions: false,
    canManageSubscriptions: false,
    canViewOffer: false,
    canManageOffer: false,
    canViewStaff: false,
    canManageStaff: false
  },
  
  // Product Manager - strategic access
  PRODUCT_MANAGER: {
    canViewUsers: true,
    canManageUsers: true,
    canViewCompanies: true,
    canManageCompanies: true,
    canViewOrders: true,
    canManageOrders: true,
    canViewProjects: true,
    canManageProjects: true,
    canViewFinancials: true,
    canManageBilling: false,
    canViewSubscriptions: true,
    canManageSubscriptions: false,
    canViewOffer: true,
    canManageOffer: false,
    canViewStaff: true,
    canManageStaff: false
  },
  
  // Data Analyst - data and project access
  DATA_ANALYST: {
    canViewUsers: true,
    canManageUsers: false,
    canViewCompanies: true,
    canManageCompanies: false,
    canViewOrders: true,
    canManageOrders: false,
    canViewProjects: true,
    canManageProjects: true,
    canViewFinancials: true, // For financial data analysis
    canManageBilling: false,
    canViewSubscriptions: true,
    canManageSubscriptions: false,
    canManageOffer: false,
    canViewOffer: false,
    canViewStaff: false,
    canManageStaff: false
  },
  
  // Admin - full administrative access (except finance)
  ADMIN: {
    canViewUsers: true,
    canManageUsers: true,
    canViewCompanies: true,
    canManageCompanies: true,
    canViewOrders: true,
    canManageOrders: true,
    canViewProjects: true,
    canManageProjects: true,
    canViewFinancials: false,
    canManageBilling: false,
    canViewSubscriptions: true,
    canManageSubscriptions: false,
    canManageOffer: true,
    canViewOffer: true,
    canViewStaff: true,
    canManageStaff: true
  },
};